import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
  AppBar as MuiAppBar,
  Box,
  Divider,
  Drawer as MuiDrawer,
  IconButton,
  Toolbar,
  Typography,
} from '@mui/material';
import { Outlet, useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
//
import { ChevronLeftIcon, MenuIcon } from 'components/common/icons';
import { useLocalStorage } from 'hooks/index';
import SidebarMenu from 'components/sidebar';
import Footer from 'components/footer';
import numberFormat from 'utils/tables/renderers/numberFormat';
import UserMenu from 'components/common/UserMenu';
import { useGetCurrentUserQuery } from '../store/api/user.api';
import { useGetPlatformSettingsQuery } from '../store/api/transaction.api';
import { signOut } from '../store/slice/auth.slice';

const drawerWidth = 240;

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 2,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ['&[data-open="true"]']: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
}));
const Drawer = styled(MuiDrawer)(({ theme }) => ({
  height: '100vh',
  '& .MuiDrawer-paper': {
    position: 'relative',
    whiteSpace: 'nowrap',
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    boxSizing: 'border-box',
    '& .MuiDivider-root + .MuiBox-root': {
      overflowY: 'auto',
      overflowX: 'clip',
    },
  },
  '&[data-open="false"] .MuiDrawer-paper': {
    overflowX: 'hidden',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(7),
    [theme.breakpoints.up('sm')]: {
      width: theme.spacing(9),
    },
  },
}));
const ToolbarIcon = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: '0 8px',
  ...theme.mixins.toolbar,
}));

const OpenDrawerButton = styled(IconButton)(() => ({
  marginRight: '36px',
  '[data-open="true"] &': {
    display: 'none',
  },
}));

const BalanceWrapper = styled(Box)(() => ({
  display: 'grid',
  gridTemplateColumns: '2fr 1fr',
  gap: '4px 8px',
  ['& .MuiTypography-root']: {
    ['&:nth-of-type(even)']: {
      textAlign: 'left',
      ['&:after']: {
        content: '"$"',
      },
    },
    ['&:nth-of-type(odd)']: {
      textAlign: 'right',
      ['&:after']: {
        content: '":"',
      },
    },
  },
}));
export default function DashboardLayout() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data: user } = useGetCurrentUserQuery();
  const { data: balance = {} } = useGetPlatformSettingsQuery(undefined);
  const [isOpen, setToggleSideBar] = useLocalStorage('openSideBar', true);

  const handleLogout = useCallback(() => {
    dispatch(signOut());
    navigate('/login');
  }, [dispatch, navigate]);

  const openDrawer = useCallback(() => {
    setToggleSideBar(true);
  }, [setToggleSideBar]);
  const closeDrawer = useCallback(() => {
    setToggleSideBar(false);
  }, [setToggleSideBar]);

  return (
    <Box display='flex'>
      <AppBar position='absolute' data-open={isOpen}>
        <Toolbar sx={{ pr: 24 }}>
          <OpenDrawerButton
            edge='start'
            color='inherit'
            aria-label='open drawer'
            onClick={openDrawer}
          >
            <MenuIcon />
          </OpenDrawerButton>
          <Typography
            component='h1'
            variant='h6'
            color='inherit'
            noWrap
            flexGrow={1}
          >
            ADMIN PANEL
          </Typography>
          <BalanceWrapper>
            <Typography component='h3' color='inherit'>
              Balance
            </Typography>
            <Typography component='h3' color='inherit'>
              {numberFormat(balance.amount)}
            </Typography>
            <Typography component='h3' color='inherit'>
              Unassigned Balance
            </Typography>
            <Typography component='h3' color='inherit'>
              {numberFormat(balance.availableUnassignedBalance)}
            </Typography>
          </BalanceWrapper>
          <UserMenu user={user} handleSignOut={handleLogout} />
        </Toolbar>
      </AppBar>
      <Drawer variant='permanent' data-open={isOpen}>
        <ToolbarIcon>
          <IconButton onClick={closeDrawer}>
            <ChevronLeftIcon />
          </IconButton>
        </ToolbarIcon>
        <Divider />
        <SidebarMenu user={user} />
      </Drawer>
      <Box
        component='main'
        display='flex'
        flexDirection='column'
        width='100%'
        flexGrow={1}
        height='100vh'
        overflow='auto'
      >
        <Box flexGrow={1} mt='80px' mb='32px'>
          <Outlet />
        </Box>
        <Footer />
      </Box>
    </Box>
  );
}
